import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { LogoMark } from '../LogoMark';
import { useWaitlist } from '../WaitlistContext';
import { LayoutDashboard, Users, ArrowLeftRight, BarChart3, Landmark, Settings, Bell, Search, LogOut, Lock } from 'lucide-react';
const SIDEBAR_LINKS = [{
  path: '/dashboard',
  label: 'Overview',
  icon: <LayoutDashboard className="w-5 h-5" data-id="element-56" />
}, {
  path: '/dashboard/accounts',
  label: 'Accounts',
  icon: <Users className="w-5 h-5" data-id="element-57" />
}, {
  path: '/dashboard/transactions',
  label: 'Transactions',
  icon: <ArrowLeftRight className="w-5 h-5" data-id="element-58" />
}, {
  path: '/dashboard/reports',
  label: 'Reports',
  icon: <BarChart3 className="w-5 h-5" data-id="element-59" />
}, {
  path: '/dashboard/settlement',
  label: 'Settlement',
  icon: <Landmark className="w-5 h-5" data-id="element-60" />
}, {
  path: '/dashboard/settings',
  label: 'Settings',
  icon: <Settings className="w-5 h-5" data-id="element-61" />
}];
export function DashboardLayout() {
  const location = useLocation();
  const {
    isUnlocked,
    count,
    target,
    remaining
  } = useWaitlist();
  return <div className="min-h-screen bg-ink flex font-sans text-bone selection:bg-mint selection:text-ink" data-id="element-62">
      {/* Sidebar */}
      <aside className="w-64 border-r border-white/5 bg-ink-2 flex flex-col hidden md:flex" data-id="element-63">
        <div className="h-16 flex items-center px-6 border-b border-white/5" data-id="element-64">
          <Link to="/" className="flex items-center gap-2 group" data-id="element-65">
            <LogoMark size={24} data-id="element-66" />
            <span className="font-semibold text-lg text-bone tracking-tight" data-id="element-67">
              Masube
            </span>
          </Link>
        </div>

        <div className="p-4" data-id="element-68">
          <div className="text-xs font-medium text-muted uppercase tracking-wider mb-2 px-2" data-id="element-69">
            Retailer Portal
          </div>
          <nav className="space-y-1" data-id="element-70">
            {SIDEBAR_LINKS.map(link => {
            const isActive = location.pathname === link.path || link.path !== '/dashboard' && location.pathname.startsWith(link.path);
            return <Link key={link.path} to={link.path} className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-mint/10 text-mint' : 'text-bone-dim hover:bg-ink-3 hover:text-bone'}`} data-id="element-71">
                  
                  {link.icon}
                  {link.label}
                </Link>;
          })}
          </nav>
        </div>

        <div className="mt-auto p-4 border-t border-white/5" data-id="element-72">
          <div className="flex items-center gap-3 px-2 py-2" data-id="element-73">
            <div className="w-8 h-8 rounded-full bg-ink-4 flex items-center justify-center text-xs font-bold text-bone" data-id="element-74">
              ED
            </div>
            <div className="flex-1 min-w-0" data-id="element-75">
              <div className="text-sm font-medium text-bone truncate" data-id="element-76">
                Edgars
              </div>
              <div className="text-xs text-muted truncate" data-id="element-77">
                Live Environment
              </div>
            </div>
            <Link to="/" className="text-muted hover:text-bone transition-colors" data-id="element-78">
              
              <LogOut className="w-4 h-4" data-id="element-79" />
            </Link>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0" data-id="element-80">
        {/* Topbar */}
        <header className="h-16 border-b border-white/5 bg-ink-2/50 backdrop-blur-sm flex items-center justify-between px-6 sticky top-0 z-10" data-id="element-81">
          <div className="flex items-center gap-4 flex-1" data-id="element-82">
            <div className="relative max-w-md w-full hidden sm:block" data-id="element-83">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" data-id="element-84" />
              <input type="text" placeholder="Search accounts, MSISDN, or refs..." className="w-full pl-10 pr-4 py-2 bg-ink border border-white/5 rounded-lg text-sm text-bone placeholder-muted focus:outline-none focus:border-mint focus:ring-1 focus:ring-mint transition-all" data-id="element-85" />
              
            </div>
          </div>
          <div className="flex items-center gap-4" data-id="element-86">
            <button className="relative p-2 text-muted hover:text-bone transition-colors" data-id="element-87">
              <Bell className="w-5 h-5" data-id="element-88" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-mint" data-id="element-89"></span>
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto relative" data-id="element-90">
          {!isUnlocked && <div className="absolute inset-0 z-30 flex items-start justify-center pt-24 px-4" data-id="element-91">
              <div className="absolute inset-0 bg-ink/70 backdrop-blur-md" data-id="element-92"></div>
              <div className="relative z-10 max-w-md text-center bg-ink-2 border border-white/10 rounded-3xl p-8 shadow-2xl" data-id="element-93">
                <div className="w-14 h-14 rounded-2xl bg-mint-soft text-mint flex items-center justify-center mx-auto mb-5" data-id="element-94">
                  <Lock className="w-7 h-7" data-id="element-95" />
                </div>
                <h3 className="text-2xl font-semibold text-bone mb-3 tracking-tight" data-id="element-96">
                  The dashboard demo unlocks at {target} retailers
                </h3>
                <p className="text-bone-dim mb-6" data-id="element-97">
                  We're at{' '}
                  <span className="text-mint font-semibold" data-id="element-98">{count}</span> of{' '}
                  {target}. Just{' '}
                  <span className="text-bone font-semibold" data-id="element-99">{remaining}</span>{' '}
                  more founding retailers and the full interactive dashboard
                  opens up for everyone on the list.
                </p>
                <a href="/#book-demo" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-mint text-ink font-semibold text-sm hover:bg-mint-dim transition-colors" data-id="element-100">
                
                  Join the waitlist to unlock
                </a>
              </div>
            </div>}

          <div className={`p-6 transition-all duration-500 ${!isUnlocked ? 'blur-sm pointer-events-none select-none opacity-60' : ''}`} aria-hidden={!isUnlocked} data-id="element-101">
            
            <Outlet data-id="element-102" />
          </div>
        </main>
      </div>
    </div>;
}